import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Car, Check, ChevronDown, Flag, Pause, Play, Route } from 'lucide-react';
import { ANNO_FONDAZIONE, annoCorrente } from '../utils/anniUnasca';
import { timelineEvents, categoryColors, categoryLabels } from '../data/timelineData';
import type { TimelineEvent } from '../data/timelineData';
import { annoRaggiunto, raggruppaPerDecennio } from '../utils/timelinePercorso';
import './TimelinePage.css';

const IMMAGINI_DECENNI = new Set([1960, 1970, 1990, 2000, 2010, 2020]);
const LARGHEZZA_STRADA = { desktop: 64, mobile: 30 };
const CURVA_DESKTOP = 70;
const X_STRADA_MOBILE = 28;
// Punto della finestra (in proporzione all'altezza) che fa da "cruscotto"
const LINEA_LETTURA = 0.6;
// Viaggio automatico: velocità in pixel al secondo e soste all'arrivo
const VELOCITA_VIAGGIO = 110;
const SOSTA_ARRIVO_MS = 1500;
const SOSTA_RIPARTENZA_MS = 2000;
const ALTEZZA_TESTATA_SITO = 80;

interface Geometria {
  larghezza: number;
  altezza: number;
  d: string;
  desktop: boolean;
}

const preferisceMenoMovimento = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Le immagini delle tappe sono quelle dei dati; i loghi vanno mostrati interi
const eUnLogo = (src: string) => /\.svg$|logo/i.test(src);

const percorsoInterno = (link: string) => link.replace(/^https?:\/\/studi\.unasca\.it/i, '');

const RulloCifra: React.FC<{ cifra: string }> = ({ cifra }) => {
  const n = /\d/.test(cifra) ? Number(cifra) : 0;
  return (
    <span className="relative block h-9 w-6 overflow-hidden rounded bg-black/60 ring-1 ring-white/10">
      <span className="rullo-cifra absolute inset-x-0 top-0" style={{ transform: `translateY(-${n * 2.25}rem)` }}>
        {Array.from({ length: 10 }, (_, i) => (
          <span key={i} className="block h-9 text-center text-xl font-bold leading-9 tabular-nums">
            {i}
          </span>
        ))}
      </span>
    </span>
  );
};

const DecennioSullaFoto: React.FC<{ etichetta: string; tappe: number; suFoto: boolean }> = ({ etichetta, tappe, suFoto }) => (
  <div className={suFoto ? 'text-white' : 'text-primary-800 dark:text-primary-300'}>
    <h2
      className="text-6xl sm:text-7xl lg:text-8xl font-extrabold leading-none tracking-tighter"
      style={suFoto ? { textShadow: '0 4px 30px rgba(0,0,0,.45)' } : undefined}
    >
      {etichetta}
    </h2>
    <div className="mt-3 flex items-center gap-3">
      <span className={`h-1 w-12 rounded-full ${suFoto ? 'bg-primary-400' : 'bg-primary-600'}`} aria-hidden="true" />
      <span className={`text-sm md:text-base font-medium ${suFoto ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'}`}>
        {tappe === 1 ? '1 tappa' : `${tappe} tappe`}
      </span>
    </div>
  </div>
);

const SchedaTappa: React.FC<{
  evento: TimelineEvent;
  sinistra: boolean;
  visibile: boolean;
  raggiunta: boolean;
}> = ({ evento, sinistra, visibile, raggiunta }) => {
  const link = evento.link ? percorsoInterno(evento.link) : null;
  return (
    <article
      className={`tappa-scheda relative rounded-2xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-black/5 dark:ring-white/10 ${
        visibile ? 'opacity-100 translate-x-0' : `opacity-0 ${sinistra ? 'lg:-translate-x-10' : 'translate-x-10'}`
      }`}
    >
      {/* Cippo chilometrico con l'anno, sul lato della strada */}
      <div
        className={`absolute -top-5 z-10 ${sinistra ? 'left-5 lg:left-auto lg:right-5' : 'left-5'}`}
        aria-hidden="true"
      >
        <div className="w-[4.5rem] overflow-hidden rounded-t-[1.4rem] rounded-b-md bg-white shadow-lg ring-1 ring-black/10">
          <div className={`h-3 ${raggiunta ? 'bg-primary-600' : 'bg-red-600'} transition-colors duration-500`} />
          <div className="py-1 text-center text-base font-bold tabular-nums text-gray-900">{evento.year}</div>
        </div>
      </div>

      {evento.image && (
        <div
          className={`relative h-44 overflow-hidden rounded-t-2xl ${
            eUnLogo(evento.image) ? 'bg-white' : 'bg-gray-100 dark:bg-gray-800'
          }`}
        >
          <img
            src={evento.image}
            alt={evento.title}
            loading="lazy"
            className={`h-full w-full ${eUnLogo(evento.image) ? 'object-contain p-8' : 'object-cover'}`}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}

      <div className={`p-6 ${evento.image ? '' : 'pt-9'}`}>
        <div className="mb-3 flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
          <span className="text-lg leading-none" aria-hidden="true">{evento.icon}</span>
          <span className={`h-2 w-2 rounded-full bg-gradient-to-br ${categoryColors[evento.category]}`} aria-hidden="true" />
          <span>{categoryLabels[evento.category]}</span>
          <span className="sr-only">, anno {evento.year}</span>
        </div>
        <h3 className="mb-2 text-lg font-bold leading-snug text-gray-900 dark:text-white">{evento.title}</h3>
        <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">{evento.description}</p>

        {evento.highlights && evento.highlights.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {evento.highlights.map((voce) => (
              <li key={voce} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                {voce}
              </li>
            ))}
          </ul>
        )}

        {link && (
          link.startsWith('/') ? (
            <Link
              to={link}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:underline underline-offset-4 dark:text-primary-400"
            >
              Approfondisci
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          ) : (
            <a
              href={link}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:underline underline-offset-4 dark:text-primary-400"
            >
              Approfondisci
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          )
        )}
      </div>
    </article>
  );
};

const TimelinePage: React.FC = () => {
  const tratti = useMemo(() => raggruppaPerDecennio(timelineEvents), []);

  const percorsoRef = useRef<HTMLDivElement>(null);
  const stradaRef = useRef<SVGPathElement>(null);
  const percorsoFattoRef = useRef<SVGPathElement>(null);
  const segnapostoRef = useRef<SVGGElement>(null);
  const barraRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const posizioniRef = useRef<{ anno: string; id: number; y: number }[]>([]);

  const [geometria, setGeometria] = useState<Geometria | null>(null);
  const [anno, setAnno] = useState(timelineEvents[0].year);
  const [raggiunte, setRaggiunte] = useState(0);
  const [cruscottoVisibile, setCruscottoVisibile] = useState(false);
  const [visibili, setVisibili] = useState<Set<number>>(() => new Set());
  const [menoMovimento] = useState(preferisceMenoMovimento);
  const [inViaggio, setInViaggio] = useState(false);
  // Il viaggio parte da solo una volta, quando si entra nella strada
  const avvioAutomaticoFattoRef = useRef(false);

  // Viaggio automatico in loop: scorre la pagina fino a "Oggi" e riparte dall'inizio del percorso.
  // Si ferma appena la persona usa rotella, tocco o tastiera.
  useEffect(() => {
    if (!inViaggio) return;
    const contenitore = percorsoRef.current;
    if (!contenitore) return;

    const inizioPercorso = () =>
      window.scrollY + contenitore.getBoundingClientRect().top - ALTEZZA_TESTATA_SITO;

    let frame = 0;
    let ultimo = performance.now();
    let resto = 0;
    let sostaFino = 0;
    let arrivoAlle = 0;
    let ripartito = false;

    if (contenitore.getBoundingClientRect().top > ALTEZZA_TESTATA_SITO) {
      window.scrollTo({ top: inizioPercorso(), behavior: 'smooth' });
      sostaFino = ultimo + 1200;
    }

    const passo = (ora: number) => {
      const dt = Math.min(64, ora - ultimo);
      ultimo = ora;
      if (arrivoAlle) {
        if (!ripartito && ora - arrivoAlle > SOSTA_ARRIVO_MS) {
          window.scrollTo({ top: inizioPercorso(), behavior: 'smooth' });
          ripartito = true;
        }
        if (ora - arrivoAlle > SOSTA_ARRIVO_MS + SOSTA_RIPARTENZA_MS) {
          arrivoAlle = 0;
          ripartito = false;
        }
      } else if (ora >= sostaFino) {
        if (contenitore.getBoundingClientRect().bottom <= window.innerHeight) {
          arrivoAlle = ora;
        } else {
          resto += (VELOCITA_VIAGGIO * dt) / 1000;
          const pixel = Math.floor(resto);
          if (pixel > 0) {
            window.scrollBy(0, pixel);
            resto -= pixel;
          }
        }
      }
      frame = requestAnimationFrame(passo);
    };
    frame = requestAnimationFrame(passo);

    const avviatoAlle = performance.now();
    const ferma = (e: Event) => {
      if ((e.target as HTMLElement | null)?.closest?.('[data-comando-viaggio]')) return;
      // Gli ultimi colpi di rotella che hanno portato alla strada non devono fermarlo subito
      if (performance.now() - avviatoAlle < 1200) return;
      setInViaggio(false);
    };
    window.addEventListener('wheel', ferma, { passive: true });
    window.addEventListener('touchstart', ferma, { passive: true });
    window.addEventListener('keydown', ferma);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', ferma);
      window.removeEventListener('touchstart', ferma);
      window.removeEventListener('keydown', ferma);
    };
  }, [inViaggio]);

  // Disegna la strada passando per ogni tappa e cartello, con curve alternate
  const misura = useCallback(() => {
    const contenitore = percorsoRef.current;
    if (!contenitore) return;
    const box = contenitore.getBoundingClientRect();
    const desktop = window.matchMedia('(min-width: 1024px)').matches;
    // Su smartphone la corsia passa esattamente per i pallini delle tappe
    const primaTappa = contenitore.querySelector<HTMLElement>('[data-anno]');
    const xTappe = primaTappa
      ? primaTappa.getBoundingClientRect().left + primaTappa.getBoundingClientRect().width / 2 - box.left
      : X_STRADA_MOBILE;
    const cx = desktop ? box.width / 2 : xTappe;
    const curva = desktop ? CURVA_DESKTOP : 0;

    const ancore = Array.from(contenitore.querySelectorAll<HTMLElement>('[data-ancora]'));
    const punti = ancore.map((el) => {
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2 - box.top;
    });
    posizioniRef.current = ancore
      .map((el, i) => ({ anno: el.dataset.anno || '', id: Number(el.dataset.id || 0), y: punti[i] }))
      .filter((p) => p.anno);

    const ys = [0, ...punti, box.height];
    let d = `M ${cx} 0`;
    for (let i = 1; i < ys.length; i++) {
      const y0 = ys[i - 1];
      const y1 = ys[i];
      const dy = y1 - y0;
      const lato = i % 2 === 0 ? 1 : -1;
      d += ` C ${cx + lato * curva} ${y0 + dy / 3}, ${cx + lato * curva} ${y1 - dy / 3}, ${cx} ${y1}`;
    }
    setGeometria({ larghezza: box.width, altezza: box.height, d, desktop });
  }, []);

  useLayoutEffect(() => {
    misura();
    const contenitore = percorsoRef.current;
    if (!contenitore || typeof ResizeObserver === 'undefined') return;
    const osservatore = new ResizeObserver(() => misura());
    osservatore.observe(contenitore);
    return () => osservatore.disconnect();
  }, [misura]);

  // Avanzamento legato allo scroll: strada percorsa, segnaposto, contachilometri, parallasse
  useEffect(() => {
    let frame = 0;
    const aggiorna = () => {
      frame = 0;
      const contenitore = percorsoRef.current;
      const strada = stradaRef.current;
      if (!contenitore || !strada) return;

      const box = contenitore.getBoundingClientRect();
      const altezzaFinestra = window.innerHeight;
      const yLettura = altezzaFinestra * LINEA_LETTURA - box.top;
      const totale = strada.getTotalLength();

      // Ricerca binaria del punto della strada all'altezza del cruscotto
      let basso = 0;
      let alto = totale;
      for (let i = 0; i < 22; i++) {
        const meta = (basso + alto) / 2;
        if (strada.getPointAtLength(meta).y < yLettura) basso = meta;
        else alto = meta;
      }
      const lunghezza = Math.max(0, Math.min(totale, basso));
      const punto = strada.getPointAtLength(lunghezza);

      percorsoFattoRef.current?.setAttribute('stroke-dasharray', `${totale} ${totale}`);
      percorsoFattoRef.current?.setAttribute('stroke-dashoffset', String(totale - lunghezza));
      segnapostoRef.current?.setAttribute('transform', `translate(${punto.x} ${punto.y})`);
      if (barraRef.current) barraRef.current.style.width = `${(lunghezza / totale) * 100}%`;

      const posizioni = posizioniRef.current;
      setAnno(annoRaggiunto(posizioni, yLettura) || posizioni[0]?.anno || timelineEvents[0].year);
      setRaggiunte(posizioni.filter((p) => p.y <= yLettura).length);
      setCruscottoVisibile(box.top < altezzaFinestra * 0.5 && box.bottom > altezzaFinestra * 0.9);

      if (!avvioAutomaticoFattoRef.current && !menoMovimento && box.top < altezzaFinestra * 0.5) {
        avvioAutomaticoFattoRef.current = true;
        setInViaggio(true);
      }

      if (!menoMovimento) {
        contenitore.querySelectorAll<HTMLElement>('[data-parallasse]').forEach((img) => {
          const r = img.parentElement!.getBoundingClientRect();
          const scarto = (r.top + r.height / 2 - altezzaFinestra / 2) * -0.18;
          img.style.transform = `translate3d(0, ${scarto}px, 0)`;
        });
      }
    };
    const richiedi = () => {
      if (!frame) frame = requestAnimationFrame(aggiorna);
    };
    richiedi();
    window.addEventListener('scroll', richiedi, { passive: true });
    window.addEventListener('resize', richiedi);
    return () => {
      window.removeEventListener('scroll', richiedi);
      window.removeEventListener('resize', richiedi);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [geometria, menoMovimento]);

  // Le tappe entrano quando arrivano a vista
  useEffect(() => {
    const contenitore = percorsoRef.current;
    if (!contenitore) return;
    const schede = Array.from(contenitore.querySelectorAll<HTMLElement>('[data-scheda]'));
    if (menoMovimento || typeof IntersectionObserver === 'undefined') {
      setVisibili(new Set(timelineEvents.map((e) => e.id)));
      return;
    }
    const osservatore = new IntersectionObserver(
      (voci) => {
        const nuove = voci.filter((v) => v.isIntersecting).map((v) => Number((v.target as HTMLElement).dataset.scheda));
        if (nuove.length) {
          setVisibili((prima) => {
            const dopo = new Set(prima);
            nuove.forEach((id) => dopo.add(id));
            return dopo;
          });
          voci.filter((v) => v.isIntersecting).forEach((v) => osservatore.unobserve(v.target));
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 },
    );
    schede.forEach((s) => osservatore.observe(s));
    return () => osservatore.disconnect();
  }, [menoMovimento]);

  // Il video parte solo se non è chiesto di ridurre il movimento
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    if (menoMovimento) video.pause();
    else video.play().catch(() => undefined);
  }, [menoMovimento]);

  const larghezzaStrada = geometria?.desktop ? LARGHEZZA_STRADA.desktop : LARGHEZZA_STRADA.mobile;
  let indiceTappa = 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Testata con il video della strada */}
      <section className="relative isolate flex min-h-[88vh] items-center overflow-hidden bg-primary-950">
        <video
          ref={videoRef}
          className="absolute inset-0 -z-10 h-full w-full object-cover"
          poster="/images/timeline-strada/testata-poster.webp"
          autoPlay={!menoMovimento}
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        >
          <source src="/videos/timeline-strada.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-48 bg-gradient-to-t from-gray-50 dark:from-gray-950 to-transparent" />

        <div className="container-custom w-full py-24">
          <div className="max-w-2xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 ring-1 ring-white/20 backdrop-blur-sm">
              <Route className="h-4 w-4 text-primary-300" aria-hidden="true" />
              <span className="text-sm font-semibold text-white">Il nostro viaggio</span>
            </div>

            <h1 className="mb-6 text-white">
              <span className="block text-4xl md:text-6xl font-bold tracking-tight leading-tight">
                Un viaggio straordinario!
              </span>
              <span className="mt-3 block text-6xl md:text-8xl font-extrabold leading-none tracking-tighter tabular-nums text-primary-400">
                {ANNO_FONDAZIONE}–{annoCorrente()}
              </span>
            </h1>

            <p className="mb-10 max-w-xl text-lg md:text-xl leading-relaxed text-white/85">
              I principali obiettivi raggiunti dell'Associazione a favore della categoria
            </p>

            <dl className="mb-10 grid max-w-xl grid-cols-3 gap-3">
              {[
                { valore: '1967', etichetta: 'Fondazione' },
                { valore: '2000+', etichetta: 'Associati' },
                { valore: '5000+', etichetta: 'Incontri istituzionali' },
              ].map(({ valore, etichetta }) => (
                <div key={etichetta} className="flex flex-col-reverse rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur-sm">
                  <dt className="text-xs md:text-sm leading-tight text-white/75">{etichetta}</dt>
                  <dd className="mb-1 text-2xl md:text-3xl font-bold text-white">{valore}</dd>
                </div>
              ))}
            </dl>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Link to="/diventa-socio" className="btn btn-primary btn-large group focus:ring-offset-primary-950">
                Unisciti a Noi
                <ArrowRight className="ml-2 h-5 w-5 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
              </Link>
              <Link
                to="/blog"
                className="btn btn-large border border-white/40 bg-transparent text-white hover:bg-white/10 focus:ring-white focus:ring-offset-primary-950"
              >
                Scopri di Più
              </Link>
              <button
                type="button"
                data-comando-viaggio
                onClick={() => {
                  avvioAutomaticoFattoRef.current = true;
                  setInViaggio(true);
                }}
                className="btn btn-large group gap-3 bg-white/10 text-white ring-1 ring-white/25 backdrop-blur-sm hover:bg-white/20 focus:ring-white focus:ring-offset-primary-950"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-primary-800 transition-transform motion-safe:group-hover:scale-110">
                  <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden="true" />
                </span>
                Guarda il viaggio
              </button>
            </div>
          </div>
        </div>

        <a
          href="#percorso"
          className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 dark:text-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          Inizia il viaggio
          <ChevronDown className="invito-scorri h-5 w-5" aria-hidden="true" />
        </a>
      </section>

      {/* Il percorso */}
      <section id="percorso" aria-label="Le tappe della storia di UNASCA" className="relative scroll-mt-16">
        <div ref={percorsoRef} className="relative pb-10">
          {geometria && (
            <svg
              className="pointer-events-none absolute inset-0 z-10"
              width={geometria.larghezza}
              height={geometria.altezza}
              viewBox={`0 0 ${geometria.larghezza} ${geometria.altezza}`}
              aria-hidden="true"
            >
              <path d={geometria.d} fill="none" strokeWidth={larghezzaStrada + 10} strokeLinecap="round" className="stroke-white dark:stroke-gray-700" />
              <path ref={stradaRef} d={geometria.d} fill="none" stroke="#2f3438" strokeWidth={larghezzaStrada} strokeLinecap="round" />
              <path
                ref={percorsoFattoRef}
                d={geometria.d}
                fill="none"
                stroke="#16a34a"
                strokeOpacity={0.35}
                strokeWidth={larghezzaStrada}
                strokeLinecap="butt"
              />
              <path
                d={geometria.d}
                fill="none"
                stroke="#ffffff"
                strokeWidth={geometria.desktop ? 3 : 2}
                strokeDasharray="14 20"
                className="strada-tratteggio"
              />
              <g ref={segnapostoRef}>
                <circle r={geometria.desktop ? 24 : 16} fill="#22c55e" className="segnaposto-onda" />
                <circle r={geometria.desktop ? 24 : 16} fill="#16a34a" stroke="#ffffff" strokeWidth={4} />
                <Car
                  x={geometria.desktop ? -12 : -9}
                  y={geometria.desktop ? -12 : -9}
                  width={geometria.desktop ? 24 : 18}
                  height={geometria.desktop ? 24 : 18}
                  color="#ffffff"
                  strokeWidth={2.25}
                />
              </g>
            </svg>
          )}

          {tratti.map((tratto) => (
            <div key={tratto.inizio}>
              {/* Ingresso nel decennio */}
              <div data-ancora className="relative my-10 h-[60vh] min-h-[22rem] max-h-[34rem] overflow-hidden">
                {/* Foto d'epoca e titolo del decennio sullo stesso strato, che scorre in parallasse */}
                <div data-parallasse className="absolute inset-x-0 -top-[15%] h-[130%] will-change-transform">
                  {IMMAGINI_DECENNI.has(tratto.inizio) && (
                    <>
                      <img
                        src={`/images/timeline-strada/anni-${tratto.inizio}.webp`}
                        srcSet={`/images/timeline-strada/anni-${tratto.inizio}-960.webp 960w, /images/timeline-strada/anni-${tratto.inizio}.webp 1920w`}
                        sizes="100vw"
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent lg:via-transparent" />
                    </>
                  )}
                  <div className="container-custom absolute inset-x-0 top-1/2 -translate-y-1/2">
                    <div className="pl-12 lg:pl-0 lg:grid lg:grid-cols-[1fr_12rem_1fr]">
                      <DecennioSullaFoto
                        etichetta={tratto.etichetta}
                        tappe={tratto.eventi.length}
                        suFoto={IMMAGINI_DECENNI.has(tratto.inizio)}
                      />
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-gray-50 via-transparent to-gray-50 dark:from-gray-950 dark:via-transparent dark:to-gray-950" />
              </div>

              {/* Tappe del decennio */}
              <div className="container-custom">
                {tratto.eventi.map((evento) => {
                  const sinistra = indiceTappa % 2 === 0;
                  const raggiunta = indiceTappa < raggiunte;
                  indiceTappa += 1;
                  return (
                    <div
                      key={evento.id}
                      className="relative grid grid-cols-[2.5rem_1fr] gap-x-4 py-7 lg:grid-cols-[1fr_12rem_1fr] lg:gap-x-0 lg:py-10"
                    >
                      <div
                        data-ancora
                        data-anno={evento.year}
                        data-id={evento.id}
                        className="relative z-20 col-start-1 row-start-1 flex items-center justify-center lg:col-start-2"
                      >
                        <span
                          className={`block h-5 w-5 rounded-full border-4 transition-colors duration-500 ${
                            raggiunta ? 'border-white bg-primary-500' : 'border-white bg-gray-400'
                          }`}
                          aria-hidden="true"
                        />
                        <span
                          className={`absolute top-1/2 hidden h-0.5 w-[calc(50%-1.25rem)] border-t-2 border-dashed border-gray-300 dark:border-gray-700 lg:block ${
                            sinistra ? 'right-1/2 mr-2.5' : 'left-1/2 ml-2.5'
                          }`}
                          aria-hidden="true"
                        />
                      </div>
                      <div
                        data-scheda={evento.id}
                        className={`relative z-20 col-start-2 row-start-1 ${sinistra ? 'lg:col-start-1' : 'lg:col-start-3'}`}
                      >
                        <SchedaTappa
                          evento={evento}
                          sinistra={sinistra}
                          visibile={visibili.has(evento.id)}
                          raggiunta={raggiunta}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Arrivo: oggi, e il viaggio continua */}
          <div className="relative z-20 py-16">
            <div className="flex flex-col items-start pl-16 lg:items-center lg:pl-0">
              <div data-ancora className="flex h-40 w-40 flex-col items-center justify-center rounded-full bg-primary-600 text-white shadow-2xl ring-8 ring-white dark:ring-gray-800">
                <Flag className="mb-1 h-7 w-7" aria-hidden="true" />
                <span className="text-3xl font-bold leading-none">Oggi</span>
                <span className="mt-1 text-xs text-white/85">il viaggio continua</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contachilometri */}
        <div
          className={`fixed bottom-5 left-5 z-40 transition-all duration-500 ${
            cruscottoVisibile || inViaggio ? 'translate-y-0 opacity-100' : 'pointer-events-none invisible translate-y-6 opacity-0'
          }`}
        >
          <div className="rounded-2xl bg-gray-900/90 px-4 py-3 text-white shadow-2xl ring-1 ring-white/10 backdrop-blur">
            <div className="mb-1.5 text-2xs font-medium text-white/60" aria-hidden="true">Stai percorrendo il</div>
            <div className="flex items-center gap-3">
              <div className="flex gap-1" aria-hidden="true">
                {anno.padStart(4, '0').split('').map((cifra, i) => (
                  <RulloCifra key={i} cifra={cifra} />
                ))}
              </div>
              <button
                type="button"
                data-comando-viaggio
                onClick={() => {
                  avvioAutomaticoFattoRef.current = true;
                  setInViaggio((v) => !v);
                }}
                aria-label={inViaggio ? 'Metti in pausa il viaggio automatico' : 'Avvia il viaggio automatico'}
                aria-pressed={inViaggio}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-600 text-white transition-colors hover:bg-primary-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {inViaggio ? (
                  <Pause className="h-4 w-4 fill-current" aria-hidden="true" />
                ) : (
                  <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden="true" />
                )}
              </button>
            </div>
            <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-white/10">
              <div ref={barraRef} className="h-full rounded-full bg-primary-500" style={{ width: 0 }} />
            </div>
          </div>
        </div>
      </section>

      {/* Conclusione */}
      <section className="py-20 bg-white dark:bg-black">
        <div className="container-custom">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-6 text-xl md:text-2xl leading-relaxed text-gray-700 dark:text-gray-300">
              Dal <strong className="text-primary-700 dark:text-primary-400">1967</strong> a oggi, UNASCA si è evoluta da semplice associazione di categoria a <strong>interlocutore istituzionale riconosciuto</strong> a livello nazionale ed europeo.
            </p>
            <p className="mb-6 text-lg md:text-xl leading-relaxed text-gray-600 dark:text-gray-400">
              Ha contribuito all'<strong>innovazione del sistema della formazione alla guida</strong>, alla <strong>digitalizzazione dei servizi automobilistici</strong> e alla promozione di una <strong>mobilità più sicura e sostenibile</strong>.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 dark:text-gray-400">
              Il suo ruolo è oggi <strong className="text-primary-700 dark:text-primary-400">strategico</strong> nella transizione verso la nuova mobilità e nel rafforzamento della <strong className="text-primary-700 dark:text-primary-400">cultura della legalità</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800 py-20">
        <div
          className="absolute inset-0 opacity-10 text-primary-300"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}
          aria-hidden="true"
        />
        <div className="container-custom relative">
          <div className="mx-auto max-w-3xl text-center text-white">
            <h2 className="mb-6 text-4xl md:text-5xl font-bold tracking-tight">Fai Parte della Storia</h2>
            <p className="mb-8 text-xl text-white/85">
              Unisciti a UNASCA e contribuisci a scrivere i prossimi capitoli della nostra storia
            </p>
            <Link to="/diventa-socio" className="btn btn-primary btn-large group focus:ring-offset-primary-900">
              Diventa Socio
              <ArrowRight className="ml-2 h-5 w-5 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TimelinePage;
